/**
 * THE PARTS NOBODY HAS SAID ANYTHING ABOUT, ALL OF THEM, IN ONE PLACE.
 *
 * 0117 added line_items.part_condition, nullable and with no default,
 * because CCR title 16 section 3356 requires each part on an invoice to
 * be stated as new, used, reconditioned, rebuilt, an OEM crash part or a
 * non-OEM aftermarket crash part, and because defaulting the column
 * would have made the database assert a fact nobody entered.
 *
 * That left 271 of 272 part lines blank, and the editor's nag only ever
 * shows the job somebody happens to have open. So the backlog sat still:
 * the column existed, the screen asked, and the number did not move.
 *
 * ─── WHY THIS IS A SCREEN AND NOT A BACKFILL ───────────────────────────
 *
 * Anthony was offered a backfill on 2026-09-18 and said build the
 * worklist instead, which is the right answer and worth writing down.
 *
 * A backfill here would have to guess, and a guess is the violation
 * itself -- printing "new" on a recycled tail lamp is precisely what
 * section 3356 exists to prevent, and it would be wrong ON PURPOSE,
 * which is worse than blank. Four of the 272 mention a condition in
 * their own description and the other 268 do not, so there is nothing to
 * derive from either.
 *
 * What the backlog actually needs is a person who knows what was fitted.
 * So this screen's whole job is to put those lines in front of that
 * person, cheaply, in an order that makes the knowing easiest: newest
 * first, because the alternator fitted last week is one somebody can
 * still remember and the one from March is not.
 *
 * ─── IT SHOWS SENT INVOICES TOO, AND DOES NOT REISSUE THEM ─────────────
 *
 * Most of these lines are on invoices already emailed. Stating the
 * condition now does not re-send anything and does not pretend the sent
 * copy said it. What it does is make the shop's own record true, which
 * is the record an inspector reads and the one the next invoice for the
 * same customer is built from.
 */

import { db } from "../data/client.ts";
import { conditionOptions, asCondition, PART_CONDITIONS } from "../domain/partcondition.ts";
import { esc, money, mount, since } from "../ui/dom.ts";
import { shopWords } from "../ui/words.ts";

type Blank = {
  id: string;
  description: string | null;
  quantity: number | null;
  total: number | null;
  part_number: string | null;
  jobs: {
    id: string; job_number: string; completed_at: string | null; created_at: string;
    customers: { full_name: string } | null;
  } | null;
};

/** How many are left, for the badge on the Everything else screen. */
export async function blankPartCount(): Promise<number> {
  try {
    const sb = await db();
    const { count } = await sb.from("line_items")
      .select("id", { count: "exact", head: true })
      .eq("kind", "part").is("part_condition", null);
    return count ?? 0;
  } catch {
    // Silence is not a state, but a badge is not the place to say so --
    // the screen itself reports a failed read properly.
    return 0;
  }
}

const PAGE = 100;
let offset = 0;

export async function partConditionsPage(): Promise<void> {
  mount(`<div class="page">
      <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
        <h1>Parts with no condition</h1>
        <p>Every part on an invoice has to say whether it is new, used,
           reconditioned, rebuilt or a crash part. These have nothing on them
           yet. Say what each one actually was &mdash; nothing here guesses,
           and leaving one blank is better than picking the wrong word.</p></header>
      <div class="card" id="pc-list"><div class="loading">Reading&hellip;</div></div>
      <div class="pager" id="pc-pager"></div>
    </div>`);
  await draw();
}

async function draw(): Promise<void> {
  const host = document.getElementById("pc-list");
  const pager = document.getElementById("pc-pager");
  if (!host) return;
  const w = shopWords();
  try {
    const sb = await db();
    const { data, error, count } = await sb.from("line_items")
      .select("id,description,quantity,total,part_number," +
              "jobs(id,job_number,completed_at,created_at,customers(full_name))",
              { count: "exact" })
      .eq("kind", "part").is("part_condition", null)
      // Newest first: a part fitted last week is one somebody can still
      // remember, and remembering is the entire input to this screen.
      .order("created_at", { ascending: false })
      .range(offset, offset + PAGE - 1);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as unknown as Blank[];
    const total = count ?? 0;

    if (!total) {
      host.innerHTML = `<div class="empty">Every ${esc(w.partLabel)} on the books has
        a condition on it. Nothing to do here.</div>`;
      if (pager) pager.innerHTML = "";
      return;
    }

    host.innerHTML =
      `<div class="card-head">${total.toLocaleString()} ${
        total === 1 ? w.partLabel : w.partsLabel} with nothing said</div>` +
      rows.map((r) => {
        const job = r.jobs;
        const when = job?.completed_at ?? job?.created_at ?? null;
        return `
        <div class="row pc-row" data-id="${esc(r.id)}">
          <div class="row-main">
            <div class="row-title">
              <span class="num">${esc(String(r.description ?? "").trim()
                || `Unnamed ${w.partLabel}`)}</span>
              ${r.part_number ? `<span class="pill mono">${esc(r.part_number)}</span>` : ""}
              ${job ? `<a class="pill" href="#/job/${esc(job.id)}">${esc(job.job_number)}</a>` : ""}
            </div>
            <div class="row-sub">${esc(job?.customers?.full_name ?? "No customer on file")}${
              when ? `<span class="dot">&middot;</span>${esc(since(when))}` : ""}${
              Number(r.quantity ?? 1) !== 1 ? `<span class="dot">&middot;</span>&times;${
                esc(String(r.quantity))}` : ""}</div>
            <div class="pc-pick">
              <select class="pc-sel" data-id="${esc(r.id)}">
                <option value="">Say what it was</option>
                ${conditionOptions().map((o) =>
                  `<option value="${esc(o.value)}">${esc(o.text)}</option>`).join("")}
              </select>
              <span class="pc-msg" data-msg="${esc(r.id)}"></span>
            </div>
          </div>
          <div class="row-side"><div class="amount">${money(Number(r.total ?? 0))}</div></div>
        </div>`;
      }).join("");

    /* Saving happens on the pick, with no Save button, because the
       alternative is a screen of a hundred dropdowns and one button at
       the bottom that loses the lot if anything goes wrong. The row
       reports for itself and stays on screen: taking it away on save
       would shift every row under the cursor mid-click. */
    for (const el of Array.from(document.querySelectorAll<HTMLSelectElement>(".pc-sel"))) {
      el.addEventListener("change", async () => {
        const id = el.dataset["id"] ?? "";
        const msg = document.querySelector<HTMLElement>(`[data-msg="${CSS.escape(id)}"]`);
        const chosen = asCondition(el.value);
        if (!chosen) return;
        el.disabled = true;
        if (msg) { msg.textContent = "Saving…"; msg.className = "pc-msg"; }
        try {
          const { error: e2 } = await sb.from("line_items")
            .update({ part_condition: chosen }).eq("id", id);
          if (e2) throw new Error(e2.message);
          if (msg) { msg.textContent = "Saved"; msg.className = "pc-msg ok"; }
          el.closest(".pc-row")?.classList.add("pc-done");
        } catch (err) {
          /* Put the box back rather than leaving a value on screen that
             is not in the database. A silent failure here is how 271
             becomes 271 again next week with everybody believing it was
             250. */
          el.disabled = false;
          el.value = "";
          if (msg) {
            msg.textContent = err instanceof Error ? err.message : String(err);
            msg.className = "pc-msg bad";
          }
        }
      });
    }

    if (pager) {
      const to = Math.min(offset + PAGE, total);
      pager.innerHTML = `
        <button class="btn sm" id="pcprev" ${offset === 0 ? "disabled" : ""}>&larr; Back</button>
        <span>${offset + 1}&ndash;${to} of ${total.toLocaleString()}</span>
        <button class="btn sm" id="pcnext" ${to >= total ? "disabled" : ""}>More &rarr;</button>`;
      document.getElementById("pcprev")?.addEventListener("click", () => {
        offset = Math.max(0, offset - PAGE); void draw();
      });
      document.getElementById("pcnext")?.addEventListener("click", () => {
        /* The list is filtered on "has no condition", so saving rows
           shrinks it under your feet and a naive next page would skip
           what moved up. Stepping back by what was just answered is the
           cheap fix; the count in the header is always read fresh. */
        const answered = document.querySelectorAll(".pc-done").length;
        offset = Math.max(0, offset + PAGE - answered);
        void draw();
      });
    }
  } catch (e) {
    host.innerHTML = `<div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div>`;
  }
}

/* Re-exported so a test can assert the screen offers every statutory
   value and no others. The list is the law's, not a preference. */
export { PART_CONDITIONS };
